import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useInspection, type Inspection } from '../../api/inspections';
import { useEnumLabel } from '../../api/enums';
import { Breadcrumbs, SummaryList, Table, Tabs, Timeline } from '../../components/idsk';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { formatDate, formatDateTime, formatTime } from '../../lib/format';
import { formatPhone } from '../../validation/rules';
import { QueryError } from '../common/QueryError';
import { LoadingPage } from '../common/LoadingPage';
import { EntityHistory } from '../history/EntityHistory';
import { StatusTag } from './StatusTag';
import { InspectionActions } from './InspectionActions';
import { InspectionFormsTab } from './forms/InspectionFormsTab';
import { InspectionDocumentsTab } from './InspectionDocumentsTab';

export function InspectionDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const query = useInspection(id);
  usePageTitle([query.data ? `${query.data.number} ${query.data.name}` : t('common.loading'), t('inspections.title')]);
  if (query.error) return <QueryError error={query.error} asPage />;
  if (query.isPending) return <LoadingPage />;
  const inspection = query.data;
  const admin = inspection.permissions.admin;

  const tabs = [
    { id: 'prehlad', label: t('inspections.tabs.overview'), panel: <Overview inspection={inspection} /> },
    { id: 'tim', label: t('inspections.tabs.team'), panel: <Team inspection={inspection} /> },
    { id: 'formulare', label: t('inspections.tabs.forms'), panel: <InspectionFormsTab inspection={inspection} /> },
    { id: 'dokumenty', label: t('inspections.tabs.documents'), panel: <InspectionDocumentsTab inspection={inspection} /> },
    ...(admin ? [{ id: 'historia', label: t('history.tab'), panel: <EntityHistory entity="inspections" id={inspection.id} /> }] : []),
  ];

  return (
    <>
      <Breadcrumbs items={[{ text: t('nav.home'), to: '/' }, { text: t('inspections.title'), to: '/inspekcie' }, { text: inspection.number }]} />
      <PageHeading caption={inspection.number}>{inspection.name}</PageHeading>
      <p className="govuk-body">
        {t('inspections.fields.status')}: <StatusTag status={inspection.status} />
      </p>
      <InspectionActions inspection={inspection} />
      <Tabs title={t('common.contents')} items={tabs} />
    </>
  );
}

function Overview({ inspection }: { inspection: Inspection }) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const leader = inspection.members.find((m) => m.leader);
  return (
    <>
      <h2 className="govuk-heading-m">{t('inspections.tabs.overview')}</h2>
      <SummaryList
        rows={[
          ...(inspection.permissions.admin ? [{ key: t('common.id'), value: <span className="app-mono">{inspection.id}</span> }] : []),
          { key: t('inspections.fields.number'), value: inspection.number },
          { key: t('inspections.fields.name'), value: inspection.name },
          {
            key: t('inspections.fields.subject'),
            value: inspection.subject ? (
              <>
                <Link className="govuk-link" to={`/subjekty/${inspection.subject.id}`}>
                  {inspection.subject.name}
                </Link>
                <br />
                {label('institutionTypes', inspection.subject.type)}, {inspection.subject.municipality}, {label('regions', inspection.subject.region)}
              </>
            ) : (
              <span className="govuk-hint govuk-!-margin-bottom-0">{t('inspections.subjectMissing')}</span>
            ),
          },
          { key: t('inspections.fields.type'), value: label('inspectionTypes', inspection.type) },
          ...(inspection.task ? [{ key: t('inspections.fields.task'), value: label('inspectionTasks', inspection.task) }] : []),
          { key: t('inspections.fields.timeframe'), value: t('wizard.step4.range', { from: formatDate(inspection.dateFrom), to: formatDate(inspection.dateTo) }) },
          { key: t('inspections.fields.status'), value: <StatusTag status={inspection.status} /> },
          { key: t('inspections.fields.leader'), value: leader?.displayName ?? '' },
          { key: t('common.updatedAt'), value: `${formatDateTime(inspection.lastModifiedAt)} (${inspection.lastModifiedBy})` },
        ]}
      />
      <h2 className="govuk-heading-m">{t('inspections.statusHistory')}</h2>
      <Timeline
        label={t('inspections.statusHistory')}
        events={[...inspection.statusHistory].reverse().map((c, i) => ({
          key: `${c.changedAt}-${i}`,
          date: formatDate(c.changedAt.slice(0, 10)),
          time: formatTime(c.changedAt),
          caption: (
            <>
              <strong>{c.from ? t('inspections.transitionCaption', { from: label('inspectionStatuses', c.from), to: label('inspectionStatuses', c.to) }) : t('inspections.createdCaption')}</strong>
              <br />
              {t('inspections.changedBy', { name: c.changedByName, role: t(`inspections.byRole.${c.byRole}`) })}
              {c.reason && (
                <>
                  <br />
                  {t('inspections.reason')}: {c.reason}
                </>
              )}
            </>
          ),
        }))}
      />
    </>
  );
}

function Team({ inspection }: { inspection: Inspection }) {
  const { t } = useTranslation();
  return (
    <Table<Inspection['members'][number]>
      caption={t('inspections.teamCaption', { count: inspection.members.length })}
      rows={inspection.members}
      rowKey={(m) => m.inspectorId}
      columns={[
        { key: 'name', header: t('inspectors.fields.name'), rowHeader: true, render: (m) => m.displayName },
        { key: 'role', header: t('inspections.fields.role'), render: (m) => (m.leader ? t('inspections.leaderRole') : t('inspections.memberRole')) },
        { key: 'email', header: t('inspectors.fields.email'), render: (m) => <a className="govuk-link" href={`mailto:${m.email}`}>{m.email}</a> },
        { key: 'phone', header: t('inspectors.fields.phone'), render: (m) => formatPhone(m.phone) },
      ]}
    />
  );
}

